import React, { useCallback, useEffect, useRef, useState } from 'react';
import CocFilter from './coc.filter';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectResult } from '../../components/select/select-chain.component';
import { SiTrackPage } from '../../components/si-track-page';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { Button, Checkbox, Dialog, FormControlLabel, FormGroup, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { cocReportColumns } from './coc.columns';
import { UserPermissionName } from '../../models/user-permission-name';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { invoiceReportApiKeyStore } from '../../api/invoice-report-api-key-store';
import useDebounce from '../../hooks/use-debounce';
import { State } from '@progress/kendo-data-query';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { WOReportRequestArgs, workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { GuardedComponent } from '../../components/guarded-component';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useFileDownload } from '../../components/file-download';
import { GenericFileUploadDialog } from '../../components/file-upload-dialog.component';
import { cocReportApiKeyStore } from '../../api/coc-report-api-key-store';
import ReportIcon from '@mui/icons-material/Report';
import ConfirmDialog from '../../components/confirm-dialog';
import CocEmailConfirmDialog from './coc-email-confirm-dialog';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAuthorize } from '../../hooks/use-authorize';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import dayjs from 'dayjs';
import { DateFormat } from '../../components/hook-form/form-date.field.component';
import { useSnackbar } from '../../hooks/use-snackbar.hook';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
  field: string;
}
interface CellProps {
  workorder: any;
}

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};

const CocPage = () => {
  const { showSnackbar } = useSnackbar();
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState<boolean>(false);
  const [unitID, setUnitID] = useState<any>();

  const defaultFromDate = dayjs().subtract(2, 'week').format(DateFormat.SIMPLE_DATE);
  const defaultToDate = dayjs().format(DateFormat.SIMPLE_DATE);

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      customerIDs: null,
      officeIDs: null,
      projectIDs: null,
      unitIDs: null,
      municipalityIDs: null,
      cocStatus: null,
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    },
  });

  const { setValue, getValues } = form;
  const hasRestoredFiltersRef = useRef(false);

  const { isLoading: isUploadFileLoading, mutateAsync: mutateUploadFileAsync } = useSaveData(
    cocReportApiKeyStore.uploadCocFile()
  );

  const [filtersState, setFiltersState] = useState<any>();

  const { isLoading, data, mutateAsync: mutateCocReportAsync } = useSaveData<any>(cocReportApiKeyStore.getCocAllReports());

  const { isLoading: isCocMunicipalityLoading, mutateAsync: mutateCocMunicipalityAsync } = useSaveData<any>(cocReportApiKeyStore.updateCocMunicipality());

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('CocReport', 'CocReport'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const { mutateAsync: mutateSaveFilters } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());

  const {
    data: savedFilterData,
    refetch: refetchSavedFilterData,
    isLoading: isSavedFilterLoading,
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('CocFilterSave', 'CocFilterSave')
  );

  const buildFilterSavePayload = useCallback(
    (filtersData?: any) => ({
      customerIDs: filtersData?.customerIDs ?? getValues('customerIDs') ?? null,
      officeIDs: filtersData?.officeIDs ?? getValues('officeIDs') ?? null,
      projectIDs: filtersData?.projectIDs ?? getValues('projectIDs') ?? null,
      unitIDs: filtersData?.unitIDs ?? getValues('unitIDs') ?? null,
      municipalityIDs: filtersData?.municipalityIDs ?? getValues('municipalityIDs') ?? null,
      cocStatus: filtersData?.cocStatus ?? getValues('cocStatus') ?? null,
      fromDate: filtersData?.fromDate ?? getValues('fromDate'),
      toDate: filtersData?.toDate ?? getValues('toDate'),
    }),
    [getValues]
  );

  const handleSaveFilter = useCallback(
    (filtersData?: any) => {
      const data = {
        filterJSON: JSON.stringify(buildFilterSavePayload(filtersData)),
        columnJSON: '',
        gridName: 'CocFilterSave',
        pageName: 'CocFilterSave',
      };

      return mutateSaveFilters({ data }).then(() => {
        refetchSavedFilterData();
      });
    },
    [buildFilterSavePayload, mutateSaveFilters, refetchSavedFilterData]
  );

  const handleSubmit = async (filtersData: any) => {
    const { fromDate, toDate } = filtersData ?? {};
    if (
      fromDate &&
      toDate &&
      dayjs(fromDate, DateFormat.SIMPLE_DATE).isAfter(dayjs(toDate, DateFormat.SIMPLE_DATE), 'day')
    ) {
      showSnackbar('Date From cannot be greater than Date To.', 'error');
      return;
    }

    const filteringData = {
      listCustomerID: filtersData?.customerIDs?.map((p: any) => p.customerID),
      listOfficeID: filtersData?.officeIDs?.map((p: any) => p.officeID),
      listProjectID: filtersData?.projectIDs?.map((p: any) => p.projectID),
      listUnitID: filtersData?.unitIDs?.map((p: any) => p.unitID),
      dateFrom: filtersData?.fromDate,
      dateTo: filtersData?.toDate,
      listMunicipalityId: filtersData?.municipalityIDs?.map((p: any) => p.municipalityID),
      listCocStatus: filtersData?.cocStatus?.map((p: any) => p.coCStatusValue),
    };

    setFiltersState(filteringData);
    await mutateCocReportAsync({ data: filteringData });
    handleSaveFilter(filtersData);
  };
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredCocReports, setFilteredCocReports] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredCocReportList = data?.data?.filter((CocReport: any) =>
      Object.values(CocReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredCocReports(filteredCocReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'CocReport',
      pageName: 'CocReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'CocReport',
      pageName: 'CocReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  useEffect(() => {
    if (isSavedFilterLoading || hasRestoredFiltersRef.current) return;

    try {
      const filterData = savedFilterData?.data?.filterJSON
        ? JSON.parse(savedFilterData.data.filterJSON)
        : null;

      setValue('customerIDs', filterData?.customerIDs ?? null);
      setValue('officeIDs', filterData?.officeIDs ?? null);
      setValue('projectIDs', filterData?.projectIDs ?? null);
      setValue('unitIDs', filterData?.unitIDs ?? null);
      setValue('municipalityIDs', filterData?.municipalityIDs ?? null);
      setValue('cocStatus', filterData?.cocStatus ?? null);
      setValue('fromDate', filterData?.fromDate || defaultFromDate);
      setValue('toDate', filterData?.toDate || defaultToDate);
    } catch (error) {
      console.log('Error parsing saved filter JSON:', error);
      setValue('customerIDs', null);
      setValue('officeIDs', null);
      setValue('projectIDs', null);
      setValue('unitIDs', null);
      setValue('municipalityIDs', null);
      setValue('cocStatus', null);
      setValue('fromDate', defaultFromDate);
      setValue('toDate', defaultToDate);
    }

    hasRestoredFiltersRef.current = true;
  }, [isSavedFilterLoading, savedFilterData?.data, setValue]);

  // ----------------------------------------------------------------------------------------
  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

  const handleDownloadFile = async (data: any) => {
    const value = {
      unitID: Number(data?.unitID) || 0,
      coCURL: data?.coCDownladURL ?? '',
      fileSection: 'cocfile',
      fileName: data?.coCDownloadfilename,
    };
    downloadFile({ additionalData: value });
  };
  const handleCoCSignedDownloadFile = async (data: any) => {
    const value = {
      unitID: Number(data?.unitID) || 0,
      coCURL: data?.coCSignedURL ?? '',
      fileSection: 'cocfile',
      fileName: data?.coCSignedDownloadfilename,
    };
    downloadFile({ additionalData: value });
  };

  const handleSaveCocFile = async (file: File | null) => {
    const formData = new FormData();
    formData.append('UnitID', unitID ?? '');
    formData.append('File', file ?? '');

    await mutateUploadFileAsync({ data: formData });
    await mutateCocReportAsync({ data: filtersState });
    setIsFileUploadDialogOpen(false);
  };

  const handleUploadClick = (cocData: any) => {
    setUnitID(cocData?.unitID);
    setIsFileUploadDialogOpen(true);
  };

  const [currentUnit, setCurrentUnit] = useState({ unitID: 0, unitName: '' });
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);

  const { isLoading: isDuxCocMailSendLoading, mutateAsync: mutateDuxCocMailSend } = useSaveData(
    cocReportApiKeyStore.duxCocMailSend()
  );

  const handleSendCocMailClick = (id: number, unitName: string) => {
    setCurrentUnit({ unitID: id, unitName: unitName });
    setIsConfirmation(true);
    // mutateDuxCocMailSend({ data });
  };

  const handleClose = () => {
    setIsConfirmation(false);
  };

  const handleConfirm = async (payloadData: any) => {
    await mutateDuxCocMailSend({ data: payloadData, }).then((data) => { });
    await mutateCocReportAsync({ data: filtersState });
    handleClose();
  };

  const JobsiteCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickJobsite = () => {
      window.open(`/units/profile/${workorder?.unitID}`, '_blank');
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.jobsite}
        </Typography>
      </td>
    );
  };

  const FileDownloadCell = ({ cocData }: { cocData: any }) => {
    const fileURL = cocData?.coCDownladURL;
    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(cocData)}>
              <Tooltip title={<Typography variant="body1">Download</Typography>}>
                <DownloadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <IconButton style={{ padding: 5 }} aria-label="view">
            <DownloadIcon style={{ color: 'gray' }} className="btnEdit" />
          </IconButton>
        )}
      </td>
    );
  };

  const FileUploadCell = ({ cocData }: { cocData: any }) => {
    const fileURL = cocData?.coCDownladURL;
    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleUploadClick(cocData)}>
              <Tooltip title={<Typography variant="body1">Upload</Typography>}>
                <FileUploadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <IconButton style={{ padding: 5 }} aria-label="view">
            <FileUploadIcon style={{ color: 'gray' }} className="btnEdit" />
          </IconButton>
        )}
      </td>
    );
  };

  const FileCoCSignedDownloadCell = ({ cocData }: { cocData: any }) => {
    const fileURL = cocData?.coCSignedURL;
    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleCoCSignedDownloadFile(cocData)}>
              <Tooltip title={<Typography variant="body1">Download</Typography>}>
                <DownloadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <IconButton style={{ padding: 5 }} aria-label="view">
            <DownloadIcon style={{ color: 'gray' }} className="btnEdit" />
          </IconButton>
        )}
      </td>
    );
  };
  const StatusCell = ({ cocData }: { cocData: any }) => {
    const hasAlert = !!cocData?.cocAlertMessage;

    const formattedCocAlertMessage = cocData?.cocAlertMessage ? cocData?.cocAlertMessage?.replace(/\./g, '.\n') : '';
    return (
      <td>
        {hasAlert ? 'Request Pending' : cocData?.cocStatus}
        {hasAlert && (
          <Tooltip
            title={
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {formattedCocAlertMessage}
              </Typography>
            }
          >
            <ReportIcon style={{ color: 'orange', marginLeft: 4, verticalAlign: 'middle', cursor: 'pointer' }} />
          </Tooltip>
        )}
      </td>
    );
  };

  const [isConfirDialog, setConfirmDialog] = useState<boolean>(false);
  const [passUnitID, setPassUnitID] = useState<any>();

  const handleUploadData = async (unitID: any) => {
    const payload = {
      unitID: unitID,
      isUploaded: true
    }
    await mutateCocMunicipalityAsync({ data: payload });
    setConfirmDialog(false);
    await mutateCocReportAsync({ data: filtersState });
  }

  const handleCheckoxChange = async (event: any, unitID: any) => {
    const isChecked: boolean = event.target.checked
    if (isChecked) {
      setConfirmDialog(true);
      setPassUnitID(unitID);
    }
  }

  const SendCocMail = ({ cocData }: { cocData: any }) => {
    const enableSendMail = cocData?.enableSendMail;
    return (
      <td>
        {/* <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_INVOICEAMOUNT]}> */}
        {enableSendMail ? (
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >

            <Button
              className="kendoGridBtn"
              type="button"
              onClick={() => handleSendCocMailClick(!cocData?.unitID ? 0 : cocData?.unitID, cocData?.unitName)}
              variant="contained"
              color="primary"
            >
              Send Mail
            </Button>
          </Grid>
        ) : (
          <Button className="kendoGridBtn" type="button" disabled variant="contained" color="primary">
            Send Mail
          </Button>
        )}
        {/* </GuardedComponent> */}
      </td>
    );
  };

  const MuiUploadCheckboxCell = ({ cocData }: { cocData: any }) => {
    const enableMuniUpload = cocData?.enableMuniUpload;
    return (
      <td>
        <FormGroup style={{ paddingLeft: 20 }}>
          <FormControlLabel control={
            <Checkbox
              checked={!enableMuniUpload && true}
              disabled={!enableMuniUpload}
              onChange={(event: any, unitID: any) => handleCheckoxChange(event, cocData?.unitID)}
            />
          } label="Uploaded to Municipality" />
        </FormGroup>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'jobsite') {
      return (dataItem: any) => {
        return <JobsiteCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'coCDownladURL') {
      return (dataItem: any) => {
        return <FileDownloadCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'coCUploadURL') {
      return (dataItem: any) => {
        return <FileUploadCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'cocStatus') {
      return (dataItem: any) => {
        return <StatusCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'coCSignedURL') {
      return (dataItem: any) => {
        return <FileCoCSignedDownloadCell cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'cocMailSend') {
      return (dataItem: any) => {
        return <SendCocMail cocData={dataItem.dataItem} />;
      };
    }
    if (field === 'enableMuniUpload') {
      return (dataItem: any) => {
        return <MuiUploadCheckboxCell cocData={dataItem.dataItem} />;
      };
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      {' '}
      <SiTrackPage title="CoC Reports">
        <ReactHookForm formName="COC_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
          <Grid container spacing={1}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <CocFilter />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Paper elevation={0} style={styling.paperStyle}>
                <SiTrackGrid
                  fileTitle='coc'
                  columns={cocReportColumns}
                  dataItemKey="unitID"
                  customCell={(field) =>
                    CustomGridCell({
                      field,
                    })
                  }
                  actionPermissions={{
                    export: UserPermissionName.DOWNLOAD_GRID,
                  }}
                  isBusy={isLoading || isLoadingGridLayout || isFileDownloadLoading || isCocMunicipalityLoading}
                  // -----------------Grid Layout-------
                  dataItems={filteredCocReports || []}
                  reorderable
                  handleSearch={handleInputChange}
                  handleSaveLayout={handleSaveLayout}
                  isSaveLayout={true}
                  gridConfigFromApi={gridConfig}
                  handleResetLayout={handleResetLayout}
                // -----------------Grid Layout-------
                />
              </Paper>
            </Grid>
          </Grid>
          {isFileUploadDialogOpen && (
            <GenericFileUploadDialog
              title="Upload CoC File"
              defaultData={{}}
              fileRequestArgs={{}}
              isLoading={isUploadFileLoading}
              onSubmit={(formData) => handleSaveCocFile(formData)}
              onClose={() => setIsFileUploadDialogOpen(false)}
              // acceptedFileTypes={{
              //   'text/csv': ['.csv'],
              //   'application/vnd.ms-excel': ['.xls'],
              //   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              // }}
              placeholder="UPLOAD FILE / DROP FILE HERE"
            />
          )}
        </ReactHookForm>
      </SiTrackPage>
      {isConfirmation && (
        <CocEmailConfirmDialog
          unitID={currentUnit?.unitID}
          message={`Are you sure that you want to send mail against the unit ${currentUnit?.unitName || ''}?`}
          onClose={handleClose}
          onConfirm={handleConfirm}
          open={isConfirmation}
          isLoading={isDuxCocMailSendLoading}
        />
      )}
      {isConfirDialog && (
        <Dialog
          open={isConfirDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px', textAlign: 'center' }}>
              <div>Are you sure you want to change the status?</div>
              <LoadingIndicator isLoading={isCocMunicipalityLoading} />
            </div>
          </div>
          <DialogActionsBar>
            {isReadOnlyPermission && (
              <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setConfirmDialog(false)}>
                Cancel
              </Button>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2, marginRight: 6
                }}
                disabled={isReadOnlyPermission}
                onClick={() => setConfirmDialog(false)}
                autoFocus
              >
                No
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2
                }}
                type="button"
                onClick={() => handleUploadData(passUnitID)}
                disabled={isLoading || isReadOnlyPermission}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default CocPage;
